<template>
  <div class="ka-outcomes">
    <div class="ka-outcomes__header">
      <h3 class="ka-outcomes__title">Session Outcomes</h3>
      <p class="ka-outcomes__subtitle">What customers did after starting a booking session on a kiosk</p>
    </div>

    <div class="ka-outcomes__total">
      <span class="ka-outcomes__total-value">{{ fmt(totals.sessions) }}</span>
      <span class="ka-outcomes__total-label">booking sessions started</span>
    </div>

    <ul class="ka-outcomes__list">
      <li v-for="row in rows" :key="row.key" class="ka-outcomes__item">
        <div class="ka-outcomes__row-top">
          <span class="ka-outcomes__dot" :style="{ background: row.color }" />
          <span class="ka-outcomes__label">{{ row.label }}</span>
          <span class="ka-outcomes__value">{{ fmt(row.value) }}</span>
          <span class="ka-outcomes__share">{{ row.share }}%</span>
        </div>
        <div
          class="ka-outcomes__track"
          role="img"
          :aria-label="`${row.label}: ${row.share}% of sessions`"
        >
          <span
            class="ka-outcomes__fill"
            :style="{ width: Math.min(100, row.share) + '%', background: row.color }"
          />
        </div>
      </li>
    </ul>

    <p class="ka-outcomes__note">
      Percentages are of booking sessions started. A session can include both a self-service
      booking and a video call, so they can add up to more than 100%.
    </p>
  </div>
</template>

<script>
import { COLORS, formatNumber, pct } from "../kioskAnalyticsHelpers";

export default {
  name: "KioskSessionOutcomes",
  props: {
    totals: { type: Object, required: true },
  },
  computed: {
    rows() {
      const t = this.totals;
      return [
        { key: "self", label: "Self service bookings", value: t.selfServiceBookings, color: COLORS.selfService },
        { key: "video", label: "Video call clicks", value: t.videoCalls, color: COLORS.videoCalls },
        { key: "completed", label: "Completed bookings", value: t.completed, color: COLORS.completed },
      ].map((r) => ({ ...r, share: pct(r.value, t.sessions) }));
    },
  },
  methods: {
    fmt: formatNumber,
  },
};
</script>

<style scoped lang="scss">
.ka-outcomes {
  background: #fff;
  border-radius: 18px;
  padding: 1.35rem 1.5rem;
  border: 1px solid #ececf2;
  box-shadow: 0 1px 2px 0 rgba(20, 21, 34, 0.04);
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &__title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2d2d2d;
    margin: 0 0 0.3rem;
  }

  &__subtitle {
    font-size: 0.8125rem;
    color: #a8a8b3;
    margin: 0 0 1.25rem;
  }

  &__total {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }

  &__total-value {
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: #16172b;
    font-variant-numeric: tabular-nums;
  }

  &__total-label {
    font-size: 0.8125rem;
    color: #6b7280;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  &__item {
    padding: 0.6rem 0;
  }

  &__row-top {
    display: grid;
    grid-template-columns: 10px 1fr auto 56px;
    align-items: center;
    gap: 0.65rem;
    font-size: 0.8125rem;
    margin-bottom: 0.45rem;
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
  }

  &__label {
    color: #5e5873;
  }

  &__value {
    font-weight: 600;
    color: #16172b;
    font-variant-numeric: tabular-nums;
  }

  &__share {
    text-align: right;
    color: #a8a8b3;
    font-variant-numeric: tabular-nums;
  }

  &__track {
    height: 8px;
    border-radius: 999px;
    background: #f3f4f6;
    overflow: hidden;
  }

  &__fill {
    display: block;
    height: 100%;
    border-radius: 999px;
    transition: width 0.4s ease;
  }

  &__note {
    margin: auto 0 0;
    padding-top: 1rem;
    font-size: 0.72rem;
    line-height: 1.4;
    color: #a8a8b3;
  }
}
</style>
